/**
 * Base-level Documents letterhead settings (phone + address).
 * Stored on `_bases/{baseKey}.documents`.
 * Falls back to legacy `purchaseOrders.phone/address` when documents is empty.
 */

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeAddress(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;
}

/**
 * @param {object|null|undefined} raw
 * @returns {{ phone: string, address: object|null }}
 */
export function normalizeDocumentLetterheadSettings(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    phone: normalizeString(source.phone),
    address: normalizeAddress(source.address),
  };
}

/**
 * Resolve letterhead phone/address for any business document.
 * Prefers `base.documents`, then legacy `base.purchaseOrders`.
 *
 * @param {object|null|undefined} base
 * @returns {{ phone: string, address: object|null }}
 */
export function getDocumentLetterheadSettings(base) {
  const fromDocuments = normalizeDocumentLetterheadSettings(base?.documents);
  if (fromDocuments.phone || fromDocuments.address) {
    return fromDocuments;
  }
  const legacy = base?.purchaseOrders;
  if (legacy && typeof legacy === 'object') {
    return normalizeDocumentLetterheadSettings(legacy);
  }
  return fromDocuments;
}

/**
 * @param {object} next
 * @param {object|null|undefined} [existingRaw]
 * @returns {{ documents: { phone: string, address: object|null } }}
 */
export function buildDocumentLetterheadSettingsPatch(next = {}, existingRaw = null) {
  const existing = normalizeDocumentLetterheadSettings(existingRaw);
  return {
    documents: {
      phone: next.phone !== undefined ? normalizeString(next.phone) : existing.phone,
      address: next.address !== undefined ? normalizeAddress(next.address) : existing.address,
    },
  };
}

/**
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function documentLetterheadSettingsEqual(a, b) {
  const left = normalizeDocumentLetterheadSettings(a);
  const right = normalizeDocumentLetterheadSettings(b);
  if (left.phone !== right.phone) return false;
  return JSON.stringify(left.address || null) === JSON.stringify(right.address || null);
}
